<template>
  <div class="oring-assistant-step4 oring-assistant__container__cards">
    <div class="card">
      <div class="section-header">
        <h3 class="section-title">
          {{ i18n.tv('installationSpaceDimensions','ORingConfigurator') }}
        </h3>
        <p class="section-description">
          {{ i18n.tv('installationSpaceDimensionsDescription','ORingConfigurator') }}
        </p>
      </div>
      <div class="tolerance-content-wrapper">
        <div class="tolerance-content">
          <div class="assistant-tolerance-content">
            <div
              v-if="assistantToleranceFields.length > 0"
              class="first-tolerance-container"
            >
              <div class="first-tolerance-field">
                <ToleranceInputRow
                  :key="assistantToleranceFields[0].key"
                  :model-value="assistantToleranceFields[0].value"
                  :label="assistantToleranceFields[0].label"
                  :tolerance-options="getToleranceOptionsForField(assistantToleranceFields[0])"
                  :tolerance-label="i18n.tv('Frontend.Report.ToleranceClass', 'ORingConfiguratorImport')"
                  :min-max-text="assistantToleranceFields[0].minMaxText"
                  :tolerance-text="assistantToleranceFields[0].toleranceText"
                  :error-msg="assistantToleranceFields[0].errorMsg || ''"
                  :readonly-dropdown="true"
                  @focus="handleFieldFocus(assistantToleranceFields[0].key)"
                  @update:model-value="handleFirstToleranceUpdate"
                />
              </div>
              <div class="first-tolerance-text">
                <p>{{ i18n.tv('shaftDiameterInfoCalc', 'ORingConfigurator') }}</p>
              </div>
              <div class="calculate-button-container">
                <ReactiveButton
                  :theme-color="'base'"
                  data-gtm="oring_calculatedimensions"
                  @click="handleCalculate"
                >
                  {{ i18n.tv('Frontend.Button.Calculate', 'ORingConfiguratorImport') }}
                </ReactiveButton>
              </div>
            </div>
            <div class="divider" />
            <div
              v-if="assistantToleranceFields.length > 1"
              class="tolerance-rows"
            >
              <ToleranceInputRow
                v-for="field in assistantToleranceFields.slice(1)"
                :key="field.key"
                :model-value="field.value"
                :label="field.label"
                :tolerance-options="getToleranceOptionsForField(field)"
                :tolerance-label="i18n.tv('Frontend.Report.ToleranceClass', 'ORingConfiguratorImport')"
                :min-max-text="field.minMaxText"
                :tolerance-text="field.toleranceText"
                :error-msg="field.errorMsg || ''"
                :readonly-dropdown="true"
                :field-key="field.key"
                @focus="handleFieldFocus(field.key)"
                @update:model-value="(value) => handleToleranceUpdate(field.key, value)"
              />
            </div>
          </div>
        </div>

        <div class="tolerance-image">
          <img loading="lazy"
            v-if="selectedImage"
            :src="selectedImage"
            alt="O-Ring Cross Section"
            class="oring-diagram"
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {useORingCalculatorStore} from '@/store/oring-calculator'
import {
  useToleranceDataManager,
  type ToleranceField
} from '~/composables/oring/useToleranceDataManager'
import {useToleranceCategoryMapping} from '~/composables/oring/useToleranceCategoryMapping'
import {useOringCalculatorService} from '~/composables/oring_calc_service'
import {useAssistantHousingValidator} from '~/composables/oring/useAssistantHousingValidator'
import type {ORingAssistantCalculateHousingRequest, ORingSearchParameter} from '@/types/oring-api'
import useI18nExtended from '~/composables/usei18nExtended'
import ToleranceInputRow from './ToleranceInputRow.vue'
import {useOringImageHelper} from '~/composables/oring/useOringImageHelper'

const i18n = useI18nExtended()

// Connect to store
const oringStore = useORingCalculatorStore()
const {oringAssistantCurrentHousing, oringAssistantGeometryId, oringAssistantCurrentStep} = storeToRefs(oringStore)

// Initialize composables
const toleranceDataManager = useToleranceDataManager()
const assistantHousingValidator = useAssistantHousingValidator()
const {createParameterCategoryMap} = useToleranceCategoryMapping()
const {getImageForGeometryAndKey} = useOringImageHelper()

// Connect tolerance data to store - computed properties that sync with store
const assistantToleranceData = computed({
  get: () => oringStore.toleranceData || {},
  set: (value: Record<string, string | null>) => {
    oringStore.toleranceData = value
  }
})

const assistantParameterInputData = computed({
  get: () => oringStore.parameterInputData || {},
  set: (value: Record<string, number | null>) => {
    oringStore.parameterInputData = value
  }
})

// Parameter category mapping for assistant fields
const parameterCategoryMap = createParameterCategoryMap()

// Label translation mapping for assistant parameters
const assistantLabelTranslationMap: Record<string, string> = {
  'd5': 'Frontend.Housing.D5',
  'd10': 'Frontend.Housing.D10',
  'd6': 'Frontend.Housing.D6',
  'b1in': 'Frontend.Housing.B1',
  'd3': 'Frontend.Housing.D3',
  'd4': 'Frontend.Housing.D4',
  'd9': 'Frontend.Housing.D9',
  'b1out': 'Frontend.Housing.B1',
  'd8': 'Frontend.Housing.D8',
  'height': 'Frontend.Housing.Height',
  'b4': 'Frontend.Housing.B4'
}

onMounted(async () => {
  await toleranceDataManager.initializeToleranceClasses()
  initializeAssistantToleranceData()
  // Get default tolerance classes with empty housing structure to populate toleranceMax values
  await performInitialHousingCalculation()
})

// Watch for changes in assistant housing
watch(oringAssistantCurrentHousing, () => {
  initializeAssistantToleranceData()
}, {immediate: true})

// Watch for changes in geometry ID which affects which parameters should be shown
watch(oringAssistantGeometryId, () => {
  initializeAssistantToleranceData()
}, {immediate: true})

// Watch for when we reach step 4 to ensure fresh default tolerance classes are loaded
watch(oringAssistantCurrentStep, async (newStep) => {
  if (newStep === 4) {
    // User has navigated to step 4, ensure we have fresh default tolerance classes
    await performInitialHousingCalculation()
  }
}, {immediate: false})

// Validation state
let checkHousingTimer: ReturnType<typeof setTimeout> | null = null
let isUnitConversionInProgress = ref(false)
const focusedField = ref<string | null>(null)

const selectedImage = computed(() => {
  return getImageForGeometryAndKey(oringAssistantGeometryId.value, focusedField.value)
})

function handleFieldFocus(fieldKey: string) {
  focusedField.value = fieldKey
}

// Watch for tolerance and parameter changes to trigger validation
watch([assistantToleranceData, assistantParameterInputData], (newValues) => {
  const [newToleranceData, newParameterInputData] = newValues

  // Skip validation if unit conversion is in progress
  if (isUnitConversionInProgress.value) {
    return
  }

  // Debounce validation calls with increased timeout to allow for unit conversion
  if (checkHousingTimer) {
    clearTimeout(checkHousingTimer)
  }
  checkHousingTimer = setTimeout(() => {
    assistantHousingValidator.checkHousing(
      newToleranceData,
      newParameterInputData,
      () => {
        // Handle validation errors - the validator handles error formatting internally
        // We don't need to do anything here as the validator's getErrorForField will return formatted messages
      },
      () => {
        // Handle successful validation (clear errors)
        // No need to clear validationErrors as the validator handles this internally
      },
      () => {
        // Handle API error - using console.error for debugging
        // eslint-disable-next-line no-console
        console.error('Assistant housing validation error')
      }
    )
  }, 750) // Increased timeout to allow unit conversion to complete
}, {deep: true})

// Initialize tolerance data from assistant housing
function initializeAssistantToleranceData() {
  if (!oringAssistantCurrentHousing.value) {
    return
  }

  // Get the parameters that should be shown based on geometry
  const searchParameters = oringStore.oringAssistantSearchParameters()

  // Start with existing store data if available - make a deep copy
  const initialToleranceData: Record<string, string | null> = {...(oringStore.toleranceData || {})}
  const initialParameterData: Record<string, number | null> = {...(oringStore.parameterInputData || {})}

  // Map assistant search parameters to tolerance fields
  searchParameters.forEach((param) => {
    const key = param.searchParameterKey
    const housing = oringAssistantCurrentHousing.value

    // Check if we already have data for this key
    const hasExistingToleranceData = initialToleranceData[key] !== undefined && initialToleranceData[key] !== null
    const hasExistingParameterData = initialParameterData[key] !== undefined && initialParameterData[key] !== null

    // Only get new values if we don't have existing ones
    if (!hasExistingToleranceData || !hasExistingParameterData) {
      // Get tolerance class ID from housing
      let toleranceClassId: string | null = null
      let parameterValue: number | null = null

      switch (key) {
      case 'd3':
        toleranceClassId = housing?.d3?.toleranceClassId?.toString() ?? null
        parameterValue = housing?.d3?.val ?? null
        break
      case 'd4':
        toleranceClassId = housing?.d4?.toleranceClassId?.toString() ?? null
        parameterValue = housing?.d4?.val ?? null
        break
      case 'd5':
        toleranceClassId = housing?.d5?.toleranceClassId?.toString() ?? null
        parameterValue = housing?.d5?.val ?? null
        break
      case 'd6':
        toleranceClassId = housing?.d6?.toleranceClassId?.toString() ?? null
        parameterValue = housing?.d6?.val ?? null
        break
      case 'd8':
        toleranceClassId = housing?.d8?.toleranceClassId?.toString() ?? null
        parameterValue = housing?.d8?.val ?? null
        break
      case 'd9':
        toleranceClassId = housing?.d9?.toleranceClassId?.toString() ?? null
        parameterValue = housing?.d9?.val ?? null
        break
      case 'd10':
        toleranceClassId = housing?.d10?.toleranceClassId?.toString() ?? null
        parameterValue = housing?.d10?.val ?? null
        break
      case 'b1in':
      case 'b1out':
        toleranceClassId = housing?.b1?.toleranceClassId?.toString() ?? null
        parameterValue = housing?.b1?.val ?? null
        break
      case 'b4':
        toleranceClassId = housing?.b4?.toleranceClassId?.toString() ?? null
        parameterValue = housing?.b4?.val ?? null
        break
      case 'height':
        toleranceClassId = housing?.h?.toleranceClassId?.toString() ?? null
        parameterValue = housing?.h?.val ?? null
        break
      }

      if (toleranceClassId && !hasExistingToleranceData) {
        initialToleranceData[key] = toleranceClassId
      }
      if (parameterValue !== null && !hasExistingParameterData) {
        initialParameterData[key] = parameterValue
      }

      // If we have both parameter value and tolerance class, make individual API call
      if (parameterValue !== null && toleranceClassId) {
        // Use a small delay to ensure store data is updated first
        nextTick(() => {
          toleranceDataManager.callGetTolerancesForField(
            key,
            parameterValue!, // Non-null assertion since we checked above
            toleranceClassId!, // Non-null assertion since we checked above
            oringAssistantCurrentHousing.value,
            100 // Shorter delay for initial loading
          )
        })
      }
    }
  })

  // Update the store directly with a full replacement to ensure reactivity
  oringStore.toleranceData = {...initialToleranceData}
  oringStore.parameterInputData = {...initialParameterData}
}

// Generate tolerance fields for display using assistant-specific parameters
const assistantToleranceFields = computed(() => {
  // Make sure this computed is reactive to geometry changes and store data
  const currentGeometryId = oringAssistantGeometryId.value
  const currentToleranceData = assistantToleranceData.value
  const currentParameterData = assistantParameterInputData.value

  // Only generate fields if we have a valid geometry
  if (!currentGeometryId) {
    return []
  }

  // Get assistant search parameters based on current geometry
  const searchParameters = oringStore.oringAssistantSearchParameters()

  const fields = searchParameters.map((param): ToleranceField => {
    const key = param.searchParameterKey

    // Helper function to get the correct tolerance mapping key based on geometry
    const getToleranceMappingKey = (paramKey: string): string => {
      // Special case for d3: use d3Groove when geometry is 9 (groove in shaft)
      if (paramKey === 'd3' && currentGeometryId === 9) {
        return 'd3Groove'
      }
      // Map special cases for parameter category lookup
      let categoryKey = paramKey
      if (paramKey === 'height') {
        categoryKey = 'h'
      } else if (paramKey === 'b1in' || paramKey === 'b1out') {
        categoryKey = 'b1'
      }
      return categoryKey
    }

    const toleranceKey = getToleranceMappingKey(key)
    const categoryId = parameterCategoryMap[toleranceKey] || 1
    const labelTranslationKey = assistantLabelTranslationMap[key] || `Frontend.Parameter.${key}`
    let currentToleranceId = toleranceDataManager.getCurrentToleranceClassId(key, currentToleranceData)

    // If no tolerance ID is set yet, try to find the appropriate default from the API response
    if (!currentToleranceId && oringStore.oringDesignCockpitDefaultTolerances) {
      // Use the same mapping as the main tolerance data manager to convert key to parameter translation key
      const keyMappingToTolerance: Record<string, string> = {
        'd1': 'd1',
        'd2': 'd2',
        'd3': 'd3',
        'd4': 'd4',
        'd5': 'd5',
        'd6': 'd6',
        'd8': 'd8',
        'd9': 'd9',
        'd10': 'd10',
        'r1': 'r1',
        'b1in': 'b1',
        'b1out': 'b1',
        'b4': 'b4',
        'gap': 'g',
        'height': 'h'
      }

      // Helper function to get the correct tolerance mapping key based on geometry
      const getToleranceMappingKey = (paramKey: string): string => {
        // Special case for d3: use d3Groove when geometry is 9 (groove in shaft)
        if (paramKey === 'd3' && currentGeometryId === 9) {
          return 'd3Groove'
        }
        return keyMappingToTolerance[paramKey] || paramKey
      }

      const toleranceKey = getToleranceMappingKey(key)
      const defaultTolerance = oringStore.oringDesignCockpitDefaultTolerances.find(
        t => t.parameterTranslationKey === toleranceKey &&
             t.geometryId === currentGeometryId &&
             t.toleranceCategoryId === categoryId
      )

      // Convert the numeric ID to string since tolerance IDs are stored as strings
      if (defaultTolerance) {
        currentToleranceId = defaultTolerance.defaultToleranceClassId.toString()
      }
    }

    return {
      key,
      value: {
        input: currentParameterData[key] ?? null,
        tolerance: toleranceDataManager.getToleranceOptionById(categoryId, currentToleranceId)
      },
      label: i18n.tv(labelTranslationKey, 'ORingConfiguratorImport'),
      minMaxText: '', // Empty for readonly mode
      toleranceText: '', // Empty for readonly mode
      categoryId,
      errorMsg: assistantHousingValidator.getErrorForField(key)
    }
  })

  return fields
})

// Get tolerance options for a field
function getToleranceOptionsForField(field: { key: string; categoryId: number }) {
  return toleranceDataManager.getToleranceOptionsByCategory(field.categoryId)
}

// Handle tolerance updates (even though readonly, this maintains the interface)
function handleToleranceUpdate(key: string, value: {input: number | null, tolerance: {text: string, value: string | null} | null}): void {
  // Update store data when tolerance inputs change
  const toleranceValue = value as {input: number | null, tolerance: {text: string, value: string | null} | null}

  // Update tolerance data (tolerance class IDs) directly in the store
  if (toleranceValue.tolerance !== undefined) {
    const newToleranceData = {...(oringStore.toleranceData || {})}
    newToleranceData[key] = toleranceValue.tolerance ? toleranceValue.tolerance.value : null
    oringStore.toleranceData = newToleranceData
  }

  // Update parameter input data (actual parameter values) directly in the store
  if (toleranceValue.input !== undefined) {
    const newParameterInputData = {...(oringStore.parameterInputData || {})}
    newParameterInputData[key] = toleranceValue.input
    oringStore.parameterInputData = newParameterInputData
  }

  // Update the housing object with the new parameter value
  updateHousingParameterValue(key, toleranceValue.input)

  // Call GetTolerances API for this specific field only (like ToleranceChecker does)
  const parameterValue = toleranceValue.input ?? assistantParameterInputData.value[key] ?? null
  const toleranceClassId = toleranceValue.tolerance?.value ?? assistantToleranceData.value[key] ?? null

  if (parameterValue !== null && toleranceClassId) {
    // Use the Assistant housing instead of Design Cockpit housing
    toleranceDataManager.callGetTolerancesForField(
      key,
      parameterValue,
      toleranceClassId,
      oringAssistantCurrentHousing.value,
      500
    )
  }
}

// Update housing object when parameter values change
function updateHousingParameterValue(key: string, value: number | null): void {
  if (!oringAssistantCurrentHousing.value) {
    return
  }

  // Create a copy of the housing to avoid direct mutation
  const updatedHousing = JSON.parse(JSON.stringify(oringAssistantCurrentHousing.value))

  // Update the appropriate field in the housing object
  switch (key) {
  case 'd3':
    if (updatedHousing.d3) {
      updatedHousing.d3.val = value
      updatedHousing.d3.parameterValue = value
    }
    break
  case 'd4':
    if (updatedHousing.d4) {
      updatedHousing.d4.val = value
      updatedHousing.d4.parameterValue = value
    }
    break
  case 'd5':
    if (updatedHousing.d5) {
      updatedHousing.d5.val = value
      updatedHousing.d5.parameterValue = value
    }
    break
  case 'd6':
    if (updatedHousing.d6) {
      updatedHousing.d6.val = value
      updatedHousing.d6.parameterValue = value
    }
    break
  case 'd8':
    if (updatedHousing.d8) {
      updatedHousing.d8.val = value
      updatedHousing.d8.parameterValue = value
    }
    break
  case 'd9':
    if (updatedHousing.d9) {
      updatedHousing.d9.val = value
      updatedHousing.d9.parameterValue = value
    }
    break
  case 'd10':
    if (updatedHousing.d10) {
      updatedHousing.d10.val = value
      updatedHousing.d10.parameterValue = value
    }
    break
  case 'b1in':
  case 'b1out':
    if (updatedHousing.b1) {
      updatedHousing.b1.val = value
      updatedHousing.b1.parameterValue = value
    }
    break
  case 'b4':
    if (updatedHousing.b4) {
      updatedHousing.b4.val = value
      updatedHousing.b4.parameterValue = value
    }
    break
  case 'height':
    if (updatedHousing.h) {
      updatedHousing.h.val = value
      updatedHousing.h.parameterValue = value
    }
    break
  case 'r1':
    if (updatedHousing.r1) {
      updatedHousing.r1.val = value
      updatedHousing.r1.parameterValue = value
    }
    break
  }

  // Update the store with the modified housing
  oringStore.oringAssistantCurrentHousing = updatedHousing
}

// Wrapper functions for template event handlers to avoid TypeScript issues
const handleFirstToleranceUpdate = (value: any) => {
  if (assistantToleranceFields.value.length > 0) {
    handleToleranceUpdate(assistantToleranceFields.value[0].key, value)
  }
}

// Perform initial housing calculation using getDefaultToleranceClassesORingAssistant to get housing structure with toleranceMax values
async function performInitialHousingCalculation(): Promise<void> {
  try {
    // Get current geometry ID
    const currentGeometryId = oringStore.oringAssistantGeometryId
    if (!currentGeometryId) {
      return
    }

    // Always call the API to get fresh default tolerance classes for the current geometry
    // This ensures we have the correct tolerance structure when returning to step 4
    const oringService = useOringCalculatorService()
    const result = await oringService.getDefaultToleranceClassesORingAssistant({
      language: useNuxtApp().$i18n.locale.value,
      imperial: oringStore.oringCalculatorImperial,
      geometryId: currentGeometryId
    })

    // Handle the result
    if (result && result.emptyHouse) {
      if (!oringAssistantCurrentHousing.value) {
        // No existing housing, clean the empty house data and use it
        const cleanedHousing = cleanEmptyHouseData(result.emptyHouse)
        oringStore.oringAssistantCurrentHousing = cleanedHousing
      } else {
        // Existing housing - merge toleranceMax values from emptyHouse while preserving user values
        mergeToleranceMaxValues(result.emptyHouse)
      }
    }

  } catch (error) {
    // Handle error silently - tolerance values will fall back to class names
  }
}

// Helper function to clean empty house data - set parameter values to null instead of 0
function cleanEmptyHouseData(emptyHouse: any): any {
  const cleaned = JSON.parse(JSON.stringify(emptyHouse))

  // List of parameter keys that can have tolerance data
  const parameterKeys = ['b1', 'b4', 'h', 'd3', 'd4', 'd5', 'd6', 'd8', 'd9', 'd10', 'r1']

  // Clean each parameter - set val and parameterValue to null if they are 0
  parameterKeys.forEach(key => {
    if (cleaned[key]) {
      // Set parameter values to null to avoid validation errors
      if (cleaned[key].val === 0) {
        cleaned[key].val = null
      }
      if (cleaned[key].parameterValue === 0) {
        cleaned[key].parameterValue = null
      }
    }
  })

  return cleaned
}

// Helper function to merge toleranceMax values from emptyHouse while preserving user input values
function mergeToleranceMaxValues(emptyHouse: any): void {
  if (!oringAssistantCurrentHousing.value || !emptyHouse) {
    return
  }

  const housing = oringAssistantCurrentHousing.value
  const parameterKeys = ['b1', 'b4', 'h', 'd3', 'd4', 'd5', 'd6', 'd8', 'd9', 'd10', 'r1']

  // Create a copy to avoid direct mutation
  const updatedHousing = JSON.parse(JSON.stringify(housing))

  // Merge toleranceMax values from emptyHouse while preserving user values
  parameterKeys.forEach(key => {
    if (emptyHouse[key] && updatedHousing[key]) {
      // Preserve user input values but update tolerance metadata
      if (emptyHouse[key].toleranceMax !== undefined) {
        updatedHousing[key].toleranceMax = emptyHouse[key].toleranceMax
      }
      if (emptyHouse[key].toleranceMin !== undefined) {
        updatedHousing[key].toleranceMin = emptyHouse[key].toleranceMin
      }
      if (emptyHouse[key].toleranceClassId !== undefined && !updatedHousing[key].toleranceClassId) {
        updatedHousing[key].toleranceClassId = emptyHouse[key].toleranceClassId
      }
    } else if (emptyHouse[key] && !updatedHousing[key]) {
      // Add missing parameter structure from emptyHouse
      updatedHousing[key] = {...emptyHouse[key]}
      // Set val and parameterValue to null to avoid validation errors
      if (updatedHousing[key].val === 0) {
        updatedHousing[key].val = null
      }
      if (updatedHousing[key].parameterValue === 0) {
        updatedHousing[key].parameterValue = null
      }
    }
  })

  // Update the store with the merged housing
  oringStore.oringAssistantCurrentHousing = updatedHousing
}

// Handle calculate button click
async function handleCalculate(): Promise<void> {
  try {
    // Get the first tolerance field, which corresponds to the shaft diameter
    const fields = assistantToleranceFields.value
    if (fields.length === 0) {
      return
    }

    // Get the dShaft value from the first field (user input)
    const firstField = fields[0]
    const dShaftValue = firstField.value.input

    if (dShaftValue === null || dShaftValue === undefined) {
      return
    }

    // Create the API request with all current parameter values
    const baseInitialValues: ORingSearchParameter[] = [
      {searchParameterKey: 'applicationID', searchValue: oringStore.oringAssistantApplicationId},
      {searchParameterKey: 'geometryID', searchValue: oringStore.oringAssistantGeometryId},
      {searchParameterKey: 'mountingOnShaft', searchValue: oringStore.oringAssistantOnShaft},
      {searchParameterKey: 'dShaft', searchValue: dShaftValue}
    ]

    // Add all current parameter values from user input
    const geometryId = oringStore.oringAssistantGeometryId
    const updatedInitialValues = [...baseInitialValues]
    let parameterKeys: string[] = []

    // Get parameter keys based on geometry (same logic as in checkHousing)
    switch (geometryId) {
    case 8: // Radial, Static - holding
      parameterKeys = ['d5', 'd10', 'd6', 'b1in']
      break
    case 9: // Radial, Dynamic and Groove in Shaft - stretching
      parameterKeys = ['d9', 'd4', 'd3', 'b1out']
      break
    case 12: // Axial, Static - both
      parameterKeys = ['d8', 'height', 'b4']
      break
    default:
      parameterKeys = []
    }

    // Add parameter values from user input (excluding the first field which is dShaft)
    parameterKeys.forEach(key => {
      // const currentValue = assistantParameterInputData.value[key]
      // Always include the parameter, using 0 for null/undefined values to match API expectations
      updatedInitialValues.push({
        searchParameterKey: key,
        searchValue: null
      })
    })

    const request: ORingAssistantCalculateHousingRequest = {
      language: useNuxtApp().$i18n.locale.value,
      imperial: oringStore.oringCalculatorImperial,
      initialValues: updatedInitialValues
    }

    // Call the API service
    const oringService = useOringCalculatorService()
    const result = await oringService.calculateHousingORingAssistant(request)

    // Handle the result
    if (result && result.assistantHousing) {
      // Update the store with the new housing data
      oringStore.oringAssistantCurrentHousing = result.assistantHousing

      // Update all tolerance fields with calculated results (except first field which keeps user input)
      await nextTick() // Ensure store updates are processed
      updateCalculatedToleranceFields(dShaftValue)
    }

  } catch (error) {
    // Handle error silently or emit an event if needed
  }
}

// Update tolerance fields with calculated housing data, preserving first field user input
async function updateCalculatedToleranceFields(preservedFirstFieldValue: number) {
  if (!oringAssistantCurrentHousing.value) {
    return
  }

  // Get current search parameters to know which fields to update
  const searchParameters = oringStore.oringAssistantSearchParameters()

  // Create copies of the current store data to update
  const updatedToleranceData: Record<string, string | null> = {...(oringStore.toleranceData || {})}
  const updatedParameterData: Record<string, number | null> = {...(oringStore.parameterInputData || {})}

  searchParameters.forEach((param, index) => {
    const key = param.searchParameterKey
    const housing = oringAssistantCurrentHousing.value

    // Skip the first field - preserve user input for shaft diameter
    if (index === 0) {
      updatedParameterData[key] = preservedFirstFieldValue
      return
    }

    // Update all other fields with calculated housing data
    let toleranceClassId: string | null = null
    let parameterValue: number | null = null

    switch (key) {
    case 'd3':
      toleranceClassId = housing?.d3?.toleranceClassId?.toString() ?? null
      parameterValue = housing?.d3?.val ?? null
      break
    case 'd4':
      toleranceClassId = housing?.d4?.toleranceClassId?.toString() ?? null
      parameterValue = housing?.d4?.val ?? null
      break
    case 'd5':
      toleranceClassId = housing?.d5?.toleranceClassId?.toString() ?? null
      parameterValue = housing?.d5?.val ?? null
      break
    case 'd6':
      toleranceClassId = housing?.d6?.toleranceClassId?.toString() ?? null
      parameterValue = housing?.d6?.val ?? null
      break
    case 'd8':
      toleranceClassId = housing?.d8?.toleranceClassId?.toString() ?? null
      parameterValue = housing?.d8?.val ?? null
      break
    case 'd9':
      toleranceClassId = housing?.d9?.toleranceClassId?.toString() ?? null
      parameterValue = housing?.d9?.val ?? null
      break
    case 'd10':
      toleranceClassId = housing?.d10?.toleranceClassId?.toString() ?? null
      parameterValue = housing?.d10?.val ?? null
      break
    case 'b1in':
    case 'b1out':
      toleranceClassId = housing?.b1?.toleranceClassId?.toString() ?? null
      parameterValue = housing?.b1?.val ?? null
      break
    case 'b4':
      toleranceClassId = housing?.b4?.toleranceClassId?.toString() ?? null
      parameterValue = housing?.b4?.val ?? null
      break
    case 'height':
      toleranceClassId = housing?.h?.toleranceClassId?.toString() ?? null
      parameterValue = housing?.h?.val ?? null
      break
    }

    // Update tolerance and parameter data for this field
    if (toleranceClassId) {
      updatedToleranceData[key] = toleranceClassId
    }
    if (parameterValue !== null) {
      updatedParameterData[key] = parameterValue
    }
  })

  // Apply the updates directly to the store
  oringStore.toleranceData = updatedToleranceData
  oringStore.parameterInputData = updatedParameterData

  // Force reactivity update
  await nextTick()
}

function validate(): boolean {
  // Check if there are any errors using the validator directly
  const hasErrors = Object.values(assistantHousingValidator.validationErrors.value || {}).some(
    errorMsg => errorMsg !== null && errorMsg !== undefined && errorMsg !== ''
  )

  // Check if any required input numbers are empty
  const hasEmptyInputs = assistantToleranceFields.value.some(field =>
    field.value.input === null || field.value.input === undefined
  )

  // Form is valid if there are no errors and no empty inputs
  return !hasErrors && !hasEmptyInputs
}

defineExpose({validate})
</script>

<style lang="scss" scoped>
.oring-assistant-step4 {
  margin: 1rem 0;

  .card {
    margin: 0;
  }

  .divider {
    margin: 1rem 0;
    width: 100%;
    height: 0.0625rem;
    background-color: $color__fst__gray__020;
    flex-shrink: 0;
  }
}

.section-header {
  margin-bottom: .5rem;
}

.section-title {
  margin-top: 0;
}

// Layout structure matching ToleranceChecker
.tolerance-content-wrapper {
  display: flex;
  align-items: center;
  gap: 2rem;
  width: 100%;
  min-height: 18.75rem; // Ensure minimum height for image alignment
}

.tolerance-content {
  flex: 0 0 auto; // Don't flex, use exact content size
  min-width: 28.125rem; // Match the blue box width
  max-width: 28.125rem; // Prevent growing beyond the blue box
}

.tolerance-image {
  flex-shrink: 0;
  width: 18.75rem;
  height: 18.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.oring-diagram {
  max-width: 18.75rem;
  max-height: 18.75rem;
  width: 100%;
  height: auto;
  object-fit: contain;
}

.first-tolerance-field {
  .tolerance-input-row {
    margin: 0;
    width: fit-content; // Only take up the space needed for content
    max-width: fit-content;

    .input-section,
    .tolerance-section {
      flex: 0 0 auto; // Don't flex, use content size
      max-width: none; // Remove max-width constraint
    }
  }
}

.assistant-tolerance-content {
  background-color: $color__content--background__secondary;
  width: 28.125rem; // Exactly 450px converted to rem
  min-width: 28.125rem; // Maintain exact width
  padding: 1rem;

  .tolerance-rows {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    width: 100%;

    .tolerance-input-row {
      margin: 0;
      width: 100%;
    }
  }

  .first-tolerance-container {
    width: 100%;
  }

  .calculate-button-container {
    .k-button {
      width: 11.6875rem; // Exactly 187px converted to rem
    }
  }
}

// Responsive design matching ToleranceChecker
@include breakpoint-down($md) {
  .divider {
    display: none; //   hide big card divider under mobile
  }

  .tolerance-content-wrapper {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
    min-height: auto;
  }

  // Make the content container fluid on small screens
  .tolerance-content {
    width: 100%;
    min-width: 0;
    max-width: none;
  }

  // Make the blue card fluid on small screens
  .assistant-tolerance-content {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    padding: 0.75rem;
    box-sizing: border-box; // include padding in width to avoid overflow
  }

  // First input row should span full width on mobile
  .first-tolerance-field {
    .tolerance-input-row {
      width: 100%;
      max-width: 100%;

      .input-section,
      .tolerance-section {
        flex: 1 1 100%;
        max-width: 100%;
        min-width: 0;
      }
    }
  }

  // Full-width button for easier tapping
  .assistant-tolerance-content .calculate-button-container .k-button {
    width: 100%;
  }

  // Give the Calculate button breathing room on mobile
  .assistant-tolerance-content .calculate-button-container {
    margin-bottom: 1rem;
    position: relative;

    // Mobile-only divider line below the button
    &::after {
      content: '';
      display: block;
      width: 100%;
      height: 0.0625rem; // same as other dividers
      background-color: $color__fst__gray__020;
      margin-top: 1.5rem;
    }
  }

  .tolerance-image {
    width: 100%;
    height: auto;
    order: -1; // Show image first on mobile
    justify-content: center;
    margin-bottom: 0.5rem;
    position: sticky; // keep the image visible while scrolling
    top: 3.25rem; // stick to top of viewport
    background-color: $color__white; // white background to hide blue content underneath
    z-index: 2; // ensure it sits above card contents
    padding-top: 0.25rem;
    padding-bottom: 0.25rem;
  }

  .oring-diagram {
    max-width: 15.625rem;
    max-height: 15.625rem;
    width: 100%;
  }
}

@include breakpoint-down($sm) {
  .tolerance-image {
    margin-bottom: 0.25rem;
    padding-top: 0.125rem;
    padding-bottom: 0.125rem;
    top: 2.5rem;
  }

  .oring-diagram {
    max-width: 12rem;
    max-height: 12rem;
  }
}
</style>
